import { fromJS } from 'immutable';
import { constants as jobCardConstants } from '@saucelabs/sl-component-job-card';

import {
  LS_DASHBOARD_FILTERS,
  FILTER_AUTOMATED_TESTS,
  FILTER_TYPE_PERFORMANCE,
  FILTER_TYPE_OWNERSHIP,
  FILTER_TYPE_STATUS,
  PERFORMANCE_DEFAULT,
} from './constants';

const { PERF_DISPLAY_OPTIONS } = jobCardConstants;

export const serializeFiltersToLS = (payload) => (
  JSON.stringify(payload.toObject())
);

// store default perf option if any invalid perf option is selected
export const validatePerfDropdown = (filters) => {
  const selectedPerfOption = filters.getIn([FILTER_AUTOMATED_TESTS, FILTER_TYPE_PERFORMANCE]);
  return !Object.values(PERF_DISPLAY_OPTIONS).includes(selectedPerfOption) ?
    filters.setIn([FILTER_AUTOMATED_TESTS, FILTER_TYPE_PERFORMANCE], PERFORMANCE_DEFAULT) :
    filters;
};

export const deserializeFiltersFromLS = (filters) => validatePerfDropdown(fromJS(filters));

export const setLocalStorageFilters = (updatedFilters) => {
  window.localStorage.setItem(
    LS_DASHBOARD_FILTERS,
    serializeFiltersToLS(updatedFilters),
  );
};

export const getMyUsernameFilter = (username) => ({
  name: username,
  label: `My tests (${username})`,
  type: 'user',
});

export const getUsernameFilter = (username) => ({
  name: username,
  label: username,
  type: 'user',
});

export const getOrganizationFilter = () => ({
  name: 'organization',
  label: 'My organization',
  type: 'organization',
});

export const getTeamsFilter = (orgTeams, filterByIds) => {
  const mapper = (team) => ({ name: team.id, label: team.name, type: 'team' });

  return filterByIds ?
    orgTeams.toJS().filter((team) => filterByIds.includes(team.id)).map(mapper) :
    orgTeams.toJS().map(mapper);
};

export const getUsersFilter = (orgUsers) => (
  orgUsers.toJS().map((owner) => ({ name: owner, label: owner, type: 'user' }))
);

export const buildOwnerFilter = ({
  isSubaccountDashboard,
  jobsTeamSharingFeatureEnabled,
  jobsTeamSharingEnabled,
  orgTeams,
  orgUsers,
  user,
  username,
}) => {
  if (isSubaccountDashboard) {
    return [{ name: username, label: username, type: 'user' }];
  }

  const filtersArray = [
    getMyUsernameFilter(username),
  ];

  if (user.isOrgAdmin()) {
    filtersArray.push(getOrganizationFilter());
    filtersArray.push(...getUsersFilter(orgUsers));

    if (jobsTeamSharingFeatureEnabled) {
      filtersArray.push(...getTeamsFilter(orgTeams));
    }
    return filtersArray;
  }

  if (!jobsTeamSharingEnabled && !jobsTeamSharingFeatureEnabled) {
    return filtersArray;
  }

  if (!jobsTeamSharingFeatureEnabled) {
    filtersArray.push(getOrganizationFilter());
    filtersArray.push(...getUsersFilter(orgUsers));
    return filtersArray;
  }

  if (jobsTeamSharingEnabled) {
    filtersArray.push(getOrganizationFilter());
    filtersArray.push(...getUsersFilter(orgUsers));
    filtersArray.push(...getTeamsFilter(orgTeams));
    return filtersArray;
  }

  const userTeamIds = user.get('teams').map((team) => team.id);
  filtersArray.push(...getTeamsFilter(orgTeams, userTeamIds));
  return filtersArray;
};

export const getOwnershipFilter = ({
  filterReference,
  filterType,
  isSubaccountDashboard,
  isOpenSauceUser,
  username,
}) => {
  // We don't want to render filters for tabs which doesnt have a filter key
  if (!filterReference?.get(filterType)) {
    return {};
  }

  if (isSubaccountDashboard || isOpenSauceUser) {
    return getMyUsernameFilter(username);
  }

  return filterReference.getIn([filterType, FILTER_TYPE_OWNERSHIP]).toObject();
};

export const noFilterInUse = ({ filters, filterType, ownership, username }) => {
  if (!filters.has(filterType)) {
    return true;
  }

  const status = filters.getIn([filterType, FILTER_TYPE_STATUS]);
  return (
    status.size === 0 &&
    (!ownership.name || ownership.name === username)
  );
};
